import { useState, useEffect, useCallback } from 'react'
import { Box, Group, Text, Pagination } from '@mantine/core'
import { WidgetCard } from './WidgetCard'
import { CardHeader } from './CardHeader'
import { CardOverlay } from './CardOverlay'
import { CardFooter } from './CardFooter'

interface DataCardProps {
  title: string
  accent?: 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'none'
  badge?: { value: string | number; color?: string }
  loading?: boolean
  collapsible?: boolean
  refreshable?: boolean
  onRefresh?: () => void
  refreshing?: boolean
  fullscreenable?: boolean
  pagination?: {
    page: number
    total: number
    pageSize: number
    onChange: (page: number) => void
  }
  extra?: React.ReactNode
  /** Corpo sem padding: so quando a tabela ou o accordion E' o corpo inteiro, ou o
   *  conteudo ja cuida do proprio respiro. O padrao e' 16px (DESIGN_SYSTEM.md:302). */
  flush?: boolean
  children: React.ReactNode
}

export function DataCard({
  title,
  accent = 'primary',
  badge,
  loading = false,
  collapsible = false,
  refreshable = false,
  onRefresh,
  refreshing = false,
  fullscreenable = false,
  pagination,
  extra,
  flush = false,
  children,
}: DataCardProps) {
  const [collapsed, setCollapsed] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && fullscreen) setFullscreen(false)
  }, [fullscreen])

  useEffect(() => {
    if (fullscreen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [fullscreen, handleKeyDown])

  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.pageSize)
    : 0

  const showFrom = pagination ? (pagination.page - 1) * pagination.pageSize + 1 : 0
  const showTo = pagination ? Math.min(pagination.page * pagination.pageSize, pagination.total) : 0

  const cardContent = (
    <WidgetCard
      accent={accent}
      p={0}
      style={fullscreen ? {
        position: 'fixed',
        inset: 16,
        zIndex: 1000,
        overflow: 'auto',
        margin: 0,
      } : undefined}
    >
      <CardHeader
        title={title}
        badge={badge}
        collapsible={collapsible}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
        refreshable={refreshable}
        refreshing={refreshing}
        onRefresh={onRefresh}
        fullscreenable={fullscreenable}
        fullscreen={fullscreen}
        onToggleFullscreen={() => setFullscreen((v) => !v)}
        extra={extra}
      />

      {!collapsed && (
        <CardOverlay loading={loading}>
          <Box data-testid="datacard-body" style={{ padding: flush ? 0 : 16 }}>{children}</Box>
        </CardOverlay>
      )}

      {!collapsed && pagination && pagination.total > 0 && (
        <CardFooter>
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Mostrando {showFrom}-{showTo} de {pagination.total}
            </Text>
            {totalPages > 1 && (
              <Pagination
                size="xs"
                total={totalPages}
                value={pagination.page}
                onChange={pagination.onChange}
              />
            )}
          </Group>
        </CardFooter>
      )}
    </WidgetCard>
  )

  if (fullscreen) {
    return (
      <>
        <Box
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999,
          }}
          onClick={() => setFullscreen(false)}
        />
        {cardContent}
      </>
    )
  }

  return cardContent
}
